// Copyright (c) 2019 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { connect } from "react-redux";
import { injectIntl } from "react-intl";
import { Chart as ChartJS, ArcElement, Tooltip, Legend, ActiveElement } from "chart.js";
import { Chart } from "react-chartjs-2";
import { SankeyController, Flow } from "chartjs-chart-sankey";

import { IAction } from "actions";
import { isSet } from "utils";
import { getEnergySourceName } from "guiutils";

import TariffCircuitListModel from "model/tariffcircuitlist";
import ChartBaseComponent, {
  IChartBaseDispatch,
  IChartBaseExtProps,
  IChartBaseProps,
  IChartBaseState,
  mapStateToProps as baseMapStateToProps,
  mapDispatchToProps as baseMapDispatchToProps,
} from "./ChartBase";
import { IState } from "states/states";
import cssContent from "content.scss";

ChartJS.register(SankeyController, Flow);

interface ISankeyChartExtProps extends IChartBaseExtProps {}

interface ISankeyChartProps extends IChartBaseProps {}

interface ISankeyChartState extends IChartBaseState {}

interface ISankeyChartDispatch extends IChartBaseDispatch {}

// extension to SankeyDataPoint --> but this type is not marked as export -> so we copy it!
interface ISankeyData {
  from: string;
  to: string;
  flow: number;
  fromColor: string;
  toColor: string;
  curSource: string;
  tooltip: string;
  label: string;
}

const mapStateToProps = (state: IState, ownProps: IChartBaseExtProps) => {
  const props = {
    ...baseMapStateToProps(state, ownProps),
  };

  return props;
};

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: ISankeyChartDispatch = {
    ...baseMapDispatchToProps(dispatch),
  };

  return dispatchProps;
};

ChartJS.register(ArcElement, Tooltip, Legend);

class SankeyChartComponent extends ChartBaseComponent<
  ISankeyChartProps & ISankeyChartExtProps & ISankeyChartDispatch,
  ISankeyChartState
> {
  state: ISankeyChartState = {
    showZeroValues: true,
  };

  private getChartData() {
    const allChartData: ISankeyData[][] = [];
    const showOnlySelectedTariffs = false;
    const allowAllHierarchies = true;
    const showAllChildren = true;
    const ignoreColoring = false;
    const tcModel = new TariffCircuitListModel(this.props.tariffCircuits, this.props.energyMeters);

    const energySources = this.props.selectedEnergySource
      ? [this.props.selectedEnergySource]
      : (tcModel.getEnergySources(false) as string[]);

    // loop thru all selected energy-sources...
    energySources.forEach((curEnergySource) => {
      // prepare now all sankey-data with the given list!
      const { tlEntries, tlOptions } = this.getSelectedTariffCircuits(
        curEnergySource,
        showOnlySelectedTariffs,
        allowAllHierarchies,
        true, // ignore categories
        showAllChildren,
        ignoreColoring,
      );

      // filter -> only entries which have a parent,
      // or
      // if only one is selected (root), and have no children!!
      const tlEntriesFiltered =
        tlEntries.length > 1
          ? tlEntries.filter((curEntry) => {
              const parentTariffCircuit = this.props.tariffCircuits.find(
                (parentEntry) => parentEntry.parent === curEntry.id,
              );
              return (
                (isSet(curEntry.parentId) && curEntry.id !== this.props.selectedHierarchyId) ||
                !isSet(parentTariffCircuit)
              );
            })
          : tlEntries;

      // get the minimum-value available (show all values below the minimum values -> 0-values -> as min-value)
      // --> better visibility on sankey-chart
      const minValue = tlEntries.reduce((prev, tariff) => {
        const value = Math.abs(tariff.value ?? 0);
        return prev > value && value !== 0 ? value : prev;
      }, 0xfffffff);

      // map the data now down to the relevant information for the sankey-chart
      const chartData = tlEntriesFiltered.map((curTariff) => {
        const parentTariffOptionsEntry = tlEntries.find(
          (entry) => entry.id === curTariff?.parentId,
        );
        const parentTariffCircuit = this.props.tariffCircuits.find(
          (curItem, curIndex) => (curItem.id ?? curIndex) === curTariff?.parentId,
        );
        const currentName = curTariff.name;
        const parentName = parentTariffCircuit?.name ?? `${currentName}: `;
        const value = curTariff.value;

        const fromLabel =
          isSet(value) && value < 0
            ? curTariff.valueText
            : parentTariffOptionsEntry?.valueText ?? curTariff.valueText;
        const toLabel =
          isSet(value) && value < 0
            ? parentTariffOptionsEntry?.valueText ?? curTariff.valueText
            : curTariff.valueText;

        const parentColor = parentTariffOptionsEntry?.color ?? cssContent.GRAPH_GRAYED_OUT;
        const currentColor = curTariff.color;

        const fromName = `${isSet(value) && value < 0 ? currentName : parentName}`;
        const toName = `${isSet(value) && value < 0 ? parentName : currentName}`;
        const from = `${fromName}: ${fromLabel}`;
        const to = `${toName}: ${toLabel}`;
        const fromColor = isSet(value) && value < 0 ? currentColor : parentColor;
        const toColor = isSet(value) && value < 0 ? parentColor : currentColor;
        const flow = Math.abs(value ?? 0) === 0 ? minValue : Math.abs(value!);
        return {
          from,
          to,
          flow,
          fromColor,
          toColor,
          curSource: curEnergySource,
          tooltip: `${fromName} -> ${toName}: ${curTariff.valueText}`,
          label: to,
        };
      });
      allChartData.push(chartData);
    });

    return allChartData;
  }

  public renderSankeys() {
    // get the chart-data for the sankey-chart...
    const activeTarrifNodes = this.getChartData();

    // render the prepared TariffNodes
    // map the node-data now to a React-Component
    return (
      <>
        {activeTarrifNodes.map((anEnergyNodeInfo, eIndex) => {
          const values: any = {};
          anEnergyNodeInfo.forEach((nodeInfo) => {
            values[nodeInfo.to] = nodeInfo.label;
          });

          return (
            anEnergyNodeInfo.length > 0 && (
              <React.Fragment key={`dk_${anEnergyNodeInfo.length}_${eIndex}`}>
                {activeTarrifNodes.length > 1 && anEnergyNodeInfo.length > 0 && (
                  <h2 className="sourceheading">
                    {getEnergySourceName(anEnergyNodeInfo[0].curSource)}
                  </h2>
                )}
                <div id="sankey" className="sankey">
                  <Chart
                    type="sankey"
                    height={this.getHeight(anEnergyNodeInfo.length)}
                    width={"100%"}
                    data={{
                      datasets: [
                        {
                          data: anEnergyNodeInfo,
                          colorFrom: (data: any) => data.raw?.fromColor as string,
                          colorTo: (data: any) => data.raw?.toColor as string,
                          nodeWidth: 5,
                          borderWidth: 0,
                          size: "max",
                          labels: values,
                        },
                      ],
                    }}
                    options={{
                      animation: false,

                      plugins: {
                        legend: {
                          display: false,
                        },
                        tooltip: {
                          enabled: true,
                          bodyColor: "#aaaaaa",
                          backgroundColor: "#444444",
                          displayColors: true,
                          callbacks: {
                            label: (tooltipItem) => {
                              return anEnergyNodeInfo[tooltipItem.dataIndex].tooltip;
                            },
                          },
                        },
                        datalabels: {
                          display: false,
                        },
                      },
                      onClick: (event, elements, chart) =>
                        this.onChartClick(elements, anEnergyNodeInfo),
                      onHover: (event, elements, chart) =>
                        this.checkMouse(elements, anEnergyNodeInfo),
                    }}
                  />
                </div>
              </React.Fragment>
            )
          );
        })}
      </>
    );
  }

  public render() {
    return this.renderSankeys();
  }

  private onChartClick(elements: ActiveElement[], anEnergyNodeInfo: ISankeyData[]) {
    // tariff clicked -> use as new root
    const clickedTariffId = elements.length ? elements[0].index : undefined;
    if (
      isSet(clickedTariffId) &&
      clickedTariffId !== this.props.selectedHierarchyId &&
      this.props.tariffCircuits.some((tariff) => tariff.parent === clickedTariffId)
    ) {
      this.props.onHierarchyChanged(this.props.selectedDashboardIndex, clickedTariffId);
    }
  }

  private checkMouse(elements: ActiveElement[], anEnergyNodeInfo: ISankeyData[]) {
    // tariff hovered -> show different mouse-pointer
    const hoveredTariffId = elements.length ? elements[0].index : undefined;
    const isClickable =
      isSet(hoveredTariffId) &&
      hoveredTariffId !== this.props.selectedHierarchyId &&
      this.props.tariffCircuits.some((tariff) => tariff.parent === hoveredTariffId);
    const element = document.getElementById("sankey")?.getElementsByTagName("canvas")[0];
    if (isSet(element)) {
      element.style.cursor = isClickable ? "pointer" : "default";
    }
  }
}

export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(SankeyChartComponent));
